import axios from 'axios';
import { getStore } from 'redux/store';
import AuthActions from 'redux/AuthRedux';
import AppActions from 'redux/AppRedux';
import { isTenDigitsNumber } from 'utils/phone';

function api_v2(token) {
  const commonHeaders = {
    'Content-Type': 'application/json',
    'x-api-key': process.env.REACT_APP_VP_API_KEY,
  };

  if (token) {
    commonHeaders.Authorization = `Bearer ${token}`;
  }

  const httpClient = axios.create({
    baseURL: process.env.REACT_APP_API_V2_URL,
  });

  httpClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response && error.response.status === 401) {
        const store = getStore();
        store.dispatch(AppActions.setToast('You are not authorized', 'danger'));
        store.dispatch(AuthActions.logout());
      }
      return Promise.reject(error);
    }
  );

  function handleError(error, onlyMessage = true) {
    if (error.response && error.response.data)
      throw onlyMessage
        ? new Error(error.response.data.message)
        : error.response.data;
    throw error;
  }

  function _getErrorFromAxiosError(e) {
    if (e.data) throw e.data;
    if (e.response) throw e.response.data;
    if (e.request) throw e.request;
    if (e.message) throw e.message;
    throw e;
  }

  // Define API methods
  const apiClient = {
    // USERS
    async userExist(phone) {
      return httpClient
        .get(`/user/exist/${phone}`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => _getErrorFromAxiosError(err));
    },

    async signup(data) {
      if (data.phone && isTenDigitsNumber(data.phone)) data.phone = `+1${data.phone}`; // US numbers only

      return httpClient
        .post(`/user/signup`, data)
        .then((response) => response.data)
        .catch((err) => _getErrorFromAxiosError(err));
    },

    async login(data) {
      if (data.phone && isTenDigitsNumber(data.phone)) data.phone = `+1${data.phone}`; // US numbers only

      return httpClient
        .post(`/user/login`, data)
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async requestSmsCode(data) {
      if (data.phone && isTenDigitsNumber(data.phone)) data.phone = `+1${data.phone}`; // US numbers only

      return httpClient
        .post(`/contact/request-code`, data, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async verifySmsCode(phone, code) {
      const data = { phone, code };
      if (data.phone && isTenDigitsNumber(data.phone)) data.phone = `+1${data.phone}`; // US numbers only

      return httpClient
        .post(`contact/sms-code`, data, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async resetPassword(data) {
      return httpClient
        .post(`/user/v2/reset-password`, data)
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async changePassword(oldPassword, newPassword) {
      return httpClient
        .patch(
          `/user/password`,
          {
            oldPassword,
            newPassword,
          },
          { headers: commonHeaders }
        )
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async updateUser(data) {
      return httpClient
        .patch(`/user`, data, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    async deleteAccount() {
      return httpClient
        .delete(`/user`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    // VENUES
    async getVenues() {
      return httpClient
        .get(`/venue`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async getVenue(venueId) {
      return httpClient
        .get(`/venue/${venueId}`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    // TICKETS
    async createTicket(venueId, params) {
      return httpClient
        .post(`/venue/${venueId}/ticket`, params, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err, false));
    },

    async addItems(venueId, checkId, params) {
      return httpClient
        .put(`/venue/${venueId}/ticket/check/${checkId}/items`, params, {
          headers: commonHeaders,
        })
        .then((response) => response.data)
        .catch((err) => handleError(err, false));
    },

    async getTicket(ticketId) {
      return httpClient
        .get(`/ticket/${ticketId}`, {
          headers: commonHeaders,
        })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    async getTicketByUser() {
      return httpClient
        .get(`ticket/current-ticket`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    async getClosedTicketSByUser() {
      return httpClient
        .get(`ticket/closed-tickets`, { headers: commonHeaders })
        .then((response) => response.data);
    },
    async getTicketByTableNumber(venueId, tableNumber) {
      return httpClient
        .get(`/venue/${venueId}/ticket/table/${tableNumber}`, {
          headers: commonHeaders,
        })
        .then((response) => response.data)
        .catch((err) => handleError(err, false));
    },

    async join(ticketId, venueId) {
      return httpClient
        .put(
          `/venue/${venueId}/ticket/${ticketId}/join`,
          {},
          { headers: commonHeaders }
        )
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    async pay(checkId, body) {
      return httpClient
        .post(`/ticket/check/${checkId}/pay`, body, {
          headers: commonHeaders,
        })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    // PAYMENTS
    async setDefaultCard(cardId) {
      return httpClient
        .post(
          `/payment/card/${cardId}/set-default`,
          {},
          { headers: commonHeaders }
        )
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async loadCards() {
      return httpClient
        .get(`/payment/card`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async saveCard(cardToken) {
      return httpClient
        .post(`/payment/card`, { cardToken }, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async preauthorizeCard(cardId) {
      return httpClient
        .post(
          `/payment/card/preauthorize`,
          {
            cardId,
          },
          { headers: commonHeaders }
        )
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },
    async deleteCard(cardId) {
      return httpClient
        .delete(`/payment/card/${cardId}`, { headers: commonHeaders })
        .then((response) => response.data)
        .catch((err) => handleError(err));
    },

    // LOYALTY
    async getLoyaltyPrograms() {
      return httpClient
        .get(`/loyalty/program`, { headers: commonHeaders })
        .then((response) => response.data);
    },

    async getLoyaltyPoints() {
      return httpClient
        .get(`/loyalty/points`, { headers: commonHeaders })
        .then((response) => response.data);
    },

    async optOutOfProgram(programId) {
      return httpClient
        .post(
          `/loyalty/program/${programId}/opt-out`,
          {},
          { headers: commonHeaders }
        )
        .then((response) => response.data);
    },

    async optIntoProgram(programId) {
      return httpClient
        .post(
          `/loyalty/program/${programId}/opt-in`,
          {},
          { headers: commonHeaders }
        )
        .then((response) => response.data);
    },

    async getPromotions({ venueId, day }) {
      let query = '';
      if (venueId) query += `&venue=${venueId}`;
      if (day) query += `&day=${day}`;
      query = query.substring(1);

      return httpClient
        .get(`/loyalty/promotion?${query}`, {
          headers: commonHeaders,
        })
        .then((response) => response.data);
    },

    async redeemPromotion(promotionId, venueId) {
      return httpClient
        .post(
          `/loyalty/promotion/${promotionId}/redeem`,
          { venueId },
          { headers: commonHeaders }
        )
        .then((response) => response.data);
    },

    async applyDiscount(discountId, checkId, itemId) {
      return httpClient
        .put(
          `/check/${checkId}/discount`,
          { discountId, itemId },
          { headers: commonHeaders }
        )
        .then((response) => response.data);
    },
    async getRewardLevels(programId) {
      return httpClient
        .post(
          `/loyalty/rewardLevels`,
          { programId },
          {
            headers: commonHeaders,
          }
        )
        .then((response) => response.data);
    },

    // LOCATION
    async getMenuItems(venueId) {
      return httpClient
        .get(`venue/${venueId}/menu`, { headers: commonHeaders })
        .then((response) => response.data.items);
    },

    async getMenuItem(venueId, itemId) {
      return httpClient
        .get(`venue/${venueId}/menu/item/${itemId}`, { headers: commonHeaders })
        .then((response) => response.data);
    },

    async getMenuCategories(venueId) {
      return httpClient
        .get(`venue/${venueId}/menu/categories`, { headers: commonHeaders })
        .then((response) => response.data);
    },
    async getLoyaltyAutoOffers(params) {
      let programId = params;
      if (typeof params === 'object' && params.programId) {
        programId = programId.programId;
      }
      // TODO: Send venueId once it is set properly
      return httpClient
        .get(`loyalty/program/${programId}/offers`, { headers: commonHeaders })
        .then((response) => response.data);
    },
    async activateOffer(offerId) {
      return httpClient
        .post(
          `loyalty/offers/${offerId}/activate`,
          {},
          { headers: commonHeaders }
        )
        .then((response) => response.data);
    },
    async getLocationIdFromQR(url) {
      // TODO: Send venueId once it is set properly
      return httpClient
        .get(`venue/decode-qr?qr=${url}`, { headers: commonHeaders })
        .then((response) => response.data).catch(console.log);
    },
    async setRegistToken(pushToken) {
      return httpClient
        .post(
          `user/register-push-token`,
          { token: pushToken },
          { headers: commonHeaders }
        )
        .then((response) => response.data);
    },
  };

  return apiClient;
}

export default api_v2;
